import React, { useState } from 'react'
import {
  Grid,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  IconButton,
} from '@the-platform-group/component-library'
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@the-platform-group/component-library/Icons'
import { StepperContainer, useStepper } from '@the-platform-group/component-library/Stepper'
import { PeopleMergeProvider, usePeopleMergeContext } from '../../contexts/PeopleMergeContext'
import SearchInputs from './SearchInputs'
import ComparisonTable from './ComparisonTable'
import MultipleJobsSection from './MultipleJobsSection'
import PreviewMergeSection from './PreviewMergeSection'
import { PEOPLE_MERGE_STEPS } from '../../constants'
import { Button } from '@the-platform-group/component-library/Button'

const MergeFlow = () => {
  const { activeStep, handleNext, handleBack, handleReset } = useStepper()
  const { mergeData, jobMappingData, reviewMutation, mergeAction } = usePeopleMergeContext()
  const { sourcePerson, targetPerson, sourceId, targetId, preview, resetMergePreview } = mergeData
  const { jobMappings, resetJobMappings } = jobMappingData
  const { fetchReview, isLoading: isReviewLoading, clearReview } = reviewMutation
  const {
    merge,
    isLoading: isMergeLoading,
    isSuccess: isMergeSuccess,
    isError: isMergeError,
    error: mergeError,
    reset: resetMergeAction,
  } = mergeAction

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const hasPreviewData = sourcePerson && targetPerson && preview
  const isLastStep = activeStep === PEOPLE_MERGE_STEPS.length - 1

  const handleNextStep = async () => {
    // When moving from step 1 (Multiple Jobs) to step 2 (Preview & Merge), call review API
    if (activeStep === 1) {
      await fetchReview({
        sourceId,
        targetId,
        jobMappings,
      })
      handleNext()
    } else {
      handleNext()
    }
  }

  const handlePreviousStep = () => {
    // Clear review data when going back from step 2
    if (activeStep === 2) {
      clearReview()
    }
    handleBack()
  }

  const handleMergeClick = () => {
    setShowConfirmDialog(true)
    // Reset previous merge state when opening dialog
    resetMergeAction()
  }

  const handleConfirmMerge = () => {
    merge({
      sourceId,
      targetId,
      jobMappings,
    })
  }

  const handleCloseConfirmDialog = () => {
    if (isMergeSuccess) {
      // Reset all state to start fresh
      resetMergePreview()
      resetJobMappings()
      clearReview()
      handleReset()
      resetMergeAction()
    }
    setShowConfirmDialog(false)
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid item>
            <ComparisonTable />
          </Grid>
        )
      case 1:
        return (
          <Grid item>
            <MultipleJobsSection />
          </Grid>
        )
      case 2:
        return (
          <Grid item>
            <PreviewMergeSection />
          </Grid>
        )
      default:
        return null
    }
  }

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item container>
        <SearchInputs disabled={activeStep > 0} />
      </Grid>
      {hasPreviewData && (
        <Grid item>
          <Container maxWidth="sm">
            <StepperContainer steps={PEOPLE_MERGE_STEPS} activeStep={activeStep} />
          </Container>
        </Grid>
      )}
      {renderStepContent()}
      {hasPreviewData && (
        <Grid item container justifyContent="space-between" spacing={2}>
          <Grid item>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePreviousStep}
                disabled={isReviewLoading || isMergeLoading}
              >
                Previous
              </Button>
            )}
          </Grid>
          <Grid item>
            {isLastStep ? (
              <Button variant="contained" color="primary" onClick={handleMergeClick}>
                Merge
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNextStep}
                disabled={isReviewLoading}
                startIcon={isReviewLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isReviewLoading ? 'Loading...' : 'Next'}
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      <Dialog open={showConfirmDialog} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h6">Merge people profile confirmation</Typography>
            </Grid>
            <Grid item>
              <IconButton size="small" onClick={handleCloseConfirmDialog} disabled={isMergeLoading}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {isMergeSuccess ? (
            // TODO: temporarily showing success in modal, this will redirect to merge table
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ alignItems: 'center' }}>
                  Merge process has been initiated successfully.
                </Alert>
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  The merge is running in the background. This may take a few moments to complete.
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Alert severity="warning" sx={{ alignItems: 'center' }}>
                  This action cannot be undone. Once merged, profiles cannot be reverted.
                </Alert>
              </Grid>
              {isMergeError && (
                <Grid item>
                  <Alert severity="error" sx={{ alignItems: 'center' }}>
                    {mergeError?.response?.data?.errors?.[0]?.message ||
                      'Failed to merge profiles. Please try again.'}
                  </Alert>
                </Grid>
              )}
              <Grid item>
                <Typography variant="body2">
                  Are you sure you want to merge these profiles?
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {isMergeSuccess ? (
            // TODO: temporarily showing success in modal, this will redirect to merge table
            <Button variant="contained" onClick={handleCloseConfirmDialog}>
              Start New Merge
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleCloseConfirmDialog}
                disabled={isMergeLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmMerge}
                disabled={isMergeLoading}
                startIcon={isMergeLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isMergeLoading ? 'Merging...' : 'Merge'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

function MergeFlowWithProvider() {
  return (
    <PeopleMergeProvider>
      <MergeFlow />
    </PeopleMergeProvider>
  )
}

export default MergeFlowWithProvider
